import { Component, OnInit, Output, EventEmitter, Inject, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GenericResponse, Select } from '@interfaces';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { UtilService } from 'src/app/services/util.service';
import { SelectedOffer } from '../../new-contract-large-accounts.model';
import { NewContractLargeAccountsService } from '../../new-contract-large-accounts.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-new-contract-select-offer',
  templateUrl: './new-contract-select-offer.component.html',
  styleUrls: ['./new-contract-select-offer.component.scss']
})
export class NewContractSelectOfferComponent implements OnInit, OnDestroy {
  private numRegex = /^(\d{1,2}(\,\d{1,3})?|\,?\d{1,2})$/;
  public offerForm: FormGroup;
  public offersList: Select[] = [];
  public showMore: boolean;
  private numElements: number;
  public approved: boolean;
  public callQuery: boolean;
  private destroyed$: Subject<boolean> = new Subject<boolean>();

  @Output() canGoNext: EventEmitter<SelectedOffer> = new EventEmitter();
  @Output() changeLoader: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private newContractLargeAccountsService: NewContractLargeAccountsService,
    private utilService: UtilService,
    private snackBar: MatSnackBar,
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService
  ) { }

  ngOnInit(): void {
    this.createForm();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private createForm(): void {
    this.offerForm = new FormGroup({
      offer: new FormControl(null, Validators.required),
      omip: new FormControl({ value: null, disabled: true }, [
        Validators.required,
        Validators.maxLength(6),
        Validators.pattern(this.numRegex),
        this.validatorMinValue(0)
      ])
    });
    this.offerForm.get('offer').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      if (value) {
        this.offerForm.get('omip').enable();
      } else {
        this.offerForm.get('omip').disable();
      }
      if (this.callQuery) {
        this.callQuery = false;
        this.approved = false;
        this.goNextEmit();
      }
    });
    this.offerForm.get('omip').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.callQuery = false;
      this.approved = false;
      this.goNextEmit();
    });
  }

  private validatorMinValue = (min: number): ValidatorFn => {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }
      return !(parseFloat(value.toString().replace(',', '.')) > min) ? { min_value_error: true } : null;
    };
  }

  public getOffers(event): void {
    if (event?.more) {
      this.numElements += 25;
    } else {
      this.numElements = 25;
    }
    this.newContractLargeAccountsService.getOffers({ pageNumber: 1, NumOfElements: this.numElements, Filter: event?.filter })
      .pipe(takeUntil(this.destroyed$)).subscribe(
        (response: GenericResponse) => {
          if (response.success) {
            this.offersList = response.data.values.map(value => {
              const auxLabel = value.nombreOferta + ' / ' + value.nombreRazonSocial;
              return {
                label: auxLabel.length > 50 ? (auxLabel.slice(0, 50) + '...') : auxLabel,
                value: value.codigoCRMOferta
              };
            });
            this.showMore = response.data.totalCount > this.numElements;
          }
        },
        error => {
          this.offersList = [];
          this.utilService.toastErrors(error);
        }
      );
  }

  public handleQuery(): void {
    this.changeLoader.emit(true);
    this.newContractLargeAccountsService
      .putQueryOffer(
        `?CodigoCRMOferta=${this.offerForm.get('offer').value}&OMIP=${parseFloat(this.offerForm.get('omip').value.replace(',', '.'))}`
      ).pipe(takeUntil(this.destroyed$)).subscribe(
        (response: GenericResponse) => {
          if (response.success) {
            this.callQuery = true;
            this.approved = response.data[0].codRespuesta === 'ACEPTADO';
            if (!this.approved) {
              const message = this.i18NextService.t('NEW_CONTRACT_LA.BUSINESS') + ' : ' + response.data[0].nombreazonSocial +
                ' , ' + this.i18NextService.t('NEW_CONTRACT_LA.ERROR') + ' : ' + response.data[0].descripcionRespuesta;
              this.snackBar.open(message, 'x', { duration: 10000, panelClass: 'bg-error' });
            }
          } else {
            this.callQuery = false;
            this.approved = false;
            this.utilService.toastErrors(response);
          }
          this.goNextEmit();
          this.changeLoader.emit(false);
        },
        error => {
          this.callQuery = false;
          this.approved = false;
          this.goNextEmit();
          this.changeLoader.emit(false);
          this.utilService.toastErrors(error);
        }
      );
  }

  private goNextEmit(): void {
    this.canGoNext.emit({
      ...{
        valid: this.approved,
        offerLabel: this.offersList.find(value => value.value === this.offerForm.get('offer').value)?.label
      },
      ...this.offerForm.getRawValue()
    });
  }
}
